import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { PdfDataDeleteService } from './pdf-data-delete.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-pdf-data-delete',
    templateUrl: './pdf-data-delete.component.html',
    styleUrls: ['./pdf-data-delete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PdfDataDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];
  deleteType = 'dd';
  processing = false;

  @Input('delete-type')
  set _delete_type(deleteType: string) {
    if (deleteType) this.deleteType = deleteType;
  }

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private pdfDataDeleteService: PdfDataDeleteService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData;
    this.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      this.gridData = rawRow.map((item: any) => item.data || item);
    } else if (rawRow && typeof rawRow === 'object') {
      this.gridData = [rawRow];
    }
    this.rowData = rawRow;
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG) return;
    if (!this.gridData || this.gridData.length === 0) return;
    this.isInitialized = true;
  }

  confirmDelete() {
    if (this.processing) return;
    this.processing = true;
    this.cdr.detectChanges();

    this.pdfDataDeleteService.deleteData(
      this.GENERAL_CONFIG.base_url,
      this.gridData,
      this.deleteType,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.processing = false;
        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data deleted successfully!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar('Something went wrong, please try again!', 'gtw-snackbar--red');
          this.requestGridUpdate.emit('cancel');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.processing = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong, please try again!', 'gtw-snackbar--red');
        this.requestGridUpdate.emit('cancel');
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
